<template>
    <div class="discussion-list-users">
        <aside class="side-bar-interactive border-right">
            <section class="side-bar-interactive__panel">
                <header class="side-bar-interactive__panel__header">
                    <img :src="$appConfig.logo.pathBlack" :alt="$appConfig.logo.alt" class="side-bar__logo" style="width: 145px" />
                </header>
                <section class="side-bar-interactive__content">
                    <section class="side-bar-interactive__content__top">
                        <h2 class="side-bar-interactive__content__title">
                            {{ $t('com.file.room-map.users-for-microphones') || 'Użytkownicy' }}
                        </h2>
                        <section class="side-bar-interactive__content__users-list">
                            <perfect-scrollbar v-if="users.length > 0">
                                <UserForMicrophoneItem 
                                    v-for="user in sortedUsers" 
                                    :key="user.id" 
                                    :user="user"
                                    :isAssigned="isUserAssignedToSelectedMic(user.id)"
                                    :isEditMode="isEditMode"
                                    :selectedMicrophoneId="selectedMicrophoneId"
                                    @click="handleUserClick(user.id)"
                                />
                            </perfect-scrollbar>
                            <div v-else class="no-users-message">
                                {{ $t('com.global.no-results') || 'Brak użytkowników' }}
                            </div>
                        </section>
                    </section>
                </section>
            </section>
        </aside>
    </div>
</template>

<script>
import UserForMicrophoneItem from "./UserForMicrophoneItem.vue";
import roomMapService from "@/services/room.map.service";
import SettingsService from "@/services/settings.service";
import AlertHelper from '@/helpers/alerts.helper.js';

export default {
    name: "UsersForMicrophonesModal",
    components: {
        UserForMicrophoneItem
    },
    props: {
        roomMapId: {
            type: [String, Number],
            default: null
        },
        selectedMicrophoneId: {
            type: [String, Number],
            default: null
        },
        microphoneUsers: {
            type: Object,
            default: () => ({})
        },
        isEditMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            users: [],
            logo: ''
        }
    },
    computed: {
        sortedUsers() {
            const users = [...this.users]
            
            // Podziel użytkowników na kategorie
            const assignedToSelectedMic = []
            const assignedToOtherMic = []
            const notAssigned = []
            
            users.forEach(user => {
                const assignedMicId = this.getAssignedMicrophoneId(user.id)
                if (assignedMicId === parseInt(this.selectedMicrophoneId)) {
                    assignedToSelectedMic.push(user)
                } else if (assignedMicId !== null) {
                    assignedToOtherMic.push(user)
                } else {
                    notAssigned.push(user)
                }
            })
            
            // Funkcja sortowania alfabetycznego
            const sortAlphabetically = (a, b) => {
                const lastNameA = (a.lastName || '').toLowerCase();
                const lastNameB = (b.lastName || '').toLowerCase();
                if (lastNameA !== lastNameB) {
                    return lastNameA.localeCompare(lastNameB);
                }
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            }
            
            // Sortuj każdą kategorię
            assignedToSelectedMic.sort(sortAlphabetically)
            assignedToOtherMic.sort(sortAlphabetically)
            notAssigned.sort(sortAlphabetically)
            
            // Zwróć: przypisani do wybranego mikrofonu, nieprzypisani, przypisani do innych
            return [...assignedToSelectedMic, ...notAssigned, ...assignedToOtherMic]
        }
    },
    watch: {
        roomMapId: {
            immediate: true,
            async handler(newVal) {
                if (newVal) {
                    await this.loadUsers();
                    await this.loadLogo();
                }
            }
        }
    },
    methods: {
        async loadUsers() {
            if (!this.roomMapId) {
                console.warn('roomMapId nie jest ustawione')
                return;
            }

            try {
                this.$store.commit("showLoader");
                const response = await roomMapService.getUsersForMicrophones(this.roomMapId)
                if (response.status === 200 && Array.isArray(response.data)) {
                    this.users = response.data.map(user => ({
                        id: user.id || user.Id,
                        name: user.name || user.Name || '',
                        lastName: user.lastName || user.LastName || '',
                        function: user.function || user.Function || '',
                        uid: user.uid || user.Uid || null
                    }))
                }
            } catch (error) {
                console.error('Błąd podczas pobierania użytkowników:', error)
                var t = this.$swal.mixin(AlertHelper.errorToastElement());
                t.fire(AlertHelper.errorToastAlert(this.$t('com.global.default-save-error'), this.$t('com.global.error')));
            }
            finally {
                this.$store.commit("hideLoader");
            }
        },
        async loadLogo() {
            try {
                if (this.$store.state.settings.runtime.clientLogoShowOw === false) {
                    return "";
                }
                var header = await SettingsService.getLogoClientImage();
                if (header.status === 200) {
                    this.logo = header.data.image;
                }
            } catch (e) {
                if (e.response && e.response.status == 401) {
                    this.$router.push({ name: 'Login' })
                }
            }
        },
        getAssignedMicrophoneId(userId) {
            for (const [micId, users] of Object.entries(this.microphoneUsers)) {
                if (users.some(u => u.id === userId)) {
                    return parseInt(micId)
                }
            }
            return null
        },
        isUserAssignedToSelectedMic(userId) {
            if (!this.selectedMicrophoneId) return false
            const users = this.microphoneUsers[this.selectedMicrophoneId] || []
            return users.some(u => u.id === userId)
        },
        handleUserClick(userId) {
            if (!this.isEditMode || !this.selectedMicrophoneId) return
            
            // Sprawdź czy użytkownik jest już przypisany do zaznaczonego mikrofonu
            const isAssigned = this.isUserAssignedToSelectedMic(userId)
            
            if (isAssigned) {
                // Jeśli jest przypisany, odepnij go
                this.$emit('remove-user-from-microphone', this.selectedMicrophoneId, userId)
            } else {
                // Jeśli nie jest przypisany, przypisz go
                this.$emit('assign-user-to-microphone', this.selectedMicrophoneId, userId)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/discussion-list-users-modal.scss';

.side-bar-interactive {
    height: 100vh;
    background: #fff;
    min-width: 319px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: sticky;
    top: 0px;
    z-index: 7;

    @media only screen and (max-width:1600px) {
        min-width: auto;
        width: 315px;
    }

    &__panel {
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        &__header {
            width: 100%;
            box-sizing: border-box;
            padding: 25px 30px;
            justify-content: space-between;
            align-items: center;
            background-color: #fff;
            position: sticky;
            top: 0;
            z-index: 6;
            text-align: center;
            border-bottom: 1px solid #dde0e7;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex-grow: 1;

        &__top {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        }

        &__title {
            text-align: center;
            font-weight: 300;
            margin-top: 10px;
        }

        &__users-list {
            flex: 1;
            overflow: hidden;

            .ps {
                height: calc(100vh - 200px);
            }

            .no-users-message {
                text-align: center;
                padding: 20px;
                color: #666;
            }
        }
    }
}

.border-right {
    border-right: 1px solid #dde0e7;
}

@media only screen and (max-width:1450px) {
    .side-bar-interactive__panel__header {
        padding: 16px 30px 15px 30px;
    }
}
</style>

